import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Factory, Truck, Ship, Plane, Boxes, Zap, Shield, Users, ArrowRight, CheckCircle2, TrendingUp, Globe } from 'lucide-react';
import { NavLink } from 'react-router-dom';

const industries = [
  {
    name: 'Manufacturing',
    description: 'Streamlined supply chain solutions for manufacturers with complex logistics requirements.',
    image: 'assets/factory-1137994_1280.jpg',
    challenges: ['Supply chain disruptions', 'Inventory management', 'Just-in-time delivery', 'Quality control'],
    solutions: ['CROSSDOCS optimization', 'Real-time tracking', 'Predictive analytics', 'Custom labeling'],
    stats: ['30% faster delivery', '25% cost reduction', '99.9% accuracy'],
    icon: Factory,
    color: 'from-blue-600 to-blue-700'
  },
  {
    name: 'E-commerce & Retail',
    description: 'Fast, reliable logistics solutions that keep pace with consumer demand and expectations.',
    image: 'assets/warehouse-8921538_1280.jpg',
    challenges: ['Same-day delivery', 'Returns processing', 'Peak season scaling', 'Customer satisfaction'],
    solutions: ['Last-mile optimization', 'Returns management', 'Peak season support', 'Real-time updates'],
    stats: ['Same-day delivery', '2-hour delivery windows', '95% customer satisfaction'],
    icon: Boxes,
    color: 'from-green-600 to-green-700'
  },
  {
    name: 'Food & Beverage',
    description: 'Temperature-controlled logistics with strict compliance and food safety standards.',
    image: 'assets/truck-1910871_1280.jpg',
    challenges: ['Temperature control', 'Food safety compliance', 'Short shelf life', 'Regulatory requirements'],
    solutions: ['Climate-controlled facilities', 'HACCP compliance', 'Cold chain monitoring', 'FDA compliance'],
    stats: ['99.99% temperature compliance', 'HACCP certified', 'FDA approved'],
    icon: Truck,
    color: 'from-orange-600 to-orange-700'
  },
  {
    name: 'Automotive',
    description: 'Precision logistics for automotive parts and finished vehicles with zero-tolerance for delays.',
    image: 'assets/warehouse-9006780_1280.jpg',
    challenges: ['Just-in-time delivery', 'Parts availability', 'Quality assurance', 'Global sourcing'],
    solutions: ['JIT optimization', 'Parts tracking', 'Quality control', 'Global logistics'],
    stats: ['<2 hour response time', 'Zero defects', 'Global coverage'],
    icon: Zap,
    color: 'from-red-600 to-red-700'
  },
  {
    name: 'Healthcare & Pharmaceuticals',
    description: 'Compliant, secure logistics for sensitive healthcare products and pharmaceuticals.',
    image: 'assets/container-789488_1280.jpg',
    challenges: ['Regulatory compliance', 'Temperature control', 'Security requirements', 'Chain of custody'],
    solutions: ['FDA compliance', 'Temperature monitoring', 'Security protocols', 'Audit trails'],
    stats: ['FDA compliant', 'HIPAA secure', 'Chain of custody'],
    icon: Shield,
    color: 'from-purple-600 to-purple-700'
  },
  {
    name: 'Technology & Electronics',
    description: 'Secure, fast logistics for high-value technology products with minimal handling.',
    image: 'assets/laptop-3196481_1280.jpg',
    challenges: ['High-value security', 'Minimal handling', 'Fast delivery', 'Global distribution'],
    solutions: ['Security protocols', 'White-glove service', 'Express options', 'Global network'],
    stats: ['100% security', 'White-glove service', 'Global reach'],
    icon: Users,
    color: 'from-indigo-600 to-indigo-700'
  }
];

const industryStats = [
  { label: 'Industries Served', value: '15+', icon: Factory },
  { label: 'Global Coverage', value: '50+', icon: Globe },
  { label: 'Success Rate', value: '99.5%', icon: TrendingUp },
  { label: 'Response Time', value: '<2hrs', icon: Zap }
];

const specializedServices = [
  {
    icon: Shield,
    title: 'Compliance & Certification',
    description: 'Industry-specific compliance including FDA, HACCP, ISO, and automotive standards'
  },
  {
    icon: Zap,
    title: 'Custom Solutions',
    description: 'Tailored logistics solutions designed for your specific industry requirements'
  },
  {
    icon: Users,
    title: 'Dedicated Teams',
    description: 'Specialized teams with deep industry knowledge and experience'
  },
  {
    icon: Globe,
    title: 'Global Reach',
    description: 'Worldwide logistics network with local expertise in key markets'
  }
];

export default function Industries() {
  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Industries We Serve — CROSSDOCS</title>
        <meta name="description" content="CROSSDOCS serves 15+ industries with specialized logistics solutions. From manufacturing to healthcare, we understand your unique challenges and deliver tailored solutions." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-black/50"></div>
        <div className="relative z-10 container-consistent text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
              Industry-Specific
              <span className="block text-primary-400">Logistics Solutions</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-200 max-w-3xl mx-auto mb-8">
              We understand the unique challenges of your industry and deliver tailored 
              logistics solutions that drive efficiency, compliance, and growth.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Industry Stats */}
      <section className="py-16 bg-white border-b border-slate-200">
        <div className="container-consistent">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {industryStats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <stat.icon className="h-6 w-6 text-primary-600" />
                </div>
                <div className="text-3xl font-bold text-slate-900 mb-1">{stat.value}</div>
                <div className="text-sm text-slate-600">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="py-20 bg-white">
        <div className="container-consistent">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              Industries We Serve
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              From manufacturing to healthcare, we have deep expertise in your industry 
              and understand the unique logistics challenges you face.
            </p>
          </motion.div>

          <div className="grid gap-8 lg:grid-cols-2">
            {industries.map((industry, index) => (
              <motion.div
                key={industry.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-slate-200"
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={industry.image}
                    alt={industry.name}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-12 h-12 bg-gradient-to-r ${industry.color} rounded-xl flex items-center justify-center`}>
                      <industry.icon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-slate-900">{industry.name}</h3>
                      <p className="text-slate-600">{industry.description}</p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-3">Key Challenges:</h4>
                      <ul className="space-y-2">
                        {industry.challenges.map((challenge) => (
                          <li key={challenge} className="flex items-start gap-2 text-sm text-slate-600">
                            <div className="w-1.5 h-1.5 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                            {challenge}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-3">Our Solutions:</h4>
                      <ul className="space-y-2">
                        {industry.solutions.map((solution) => (
                          <li key={solution} className="flex items-start gap-2 text-sm text-slate-600">
                            <CheckCircle2 className="h-4 w-4 text-primary-600 flex-shrink-0 mt-0.5" />
                            {solution}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h4 className="font-semibold text-slate-900 mb-3">Results:</h4>
                    <div className="flex flex-wrap gap-2">
                      {industry.stats.map((stat) => (
                        <span
                          key={stat}
                          className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-sm rounded-full"
                        >
                          {stat}
                        </span>
                      ))}
                    </div>
                  </div>

                  <NavLink
                    to="/contact"
                    className="inline-flex items-center gap-2 text-primary-600 font-semibold hover:text-primary-700 transition-colors duration-200"
                  >
                    Learn More
                    <ArrowRight className="h-4 w-4" />
                  </NavLink>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Specialized Services */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-white">
        <div className="container-consistent">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              Specialized Services
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Beyond standard logistics, we provide industry-specific services that 
              address your unique requirements and compliance needs.
            </p>
          </motion.div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {specializedServices.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-6 border border-slate-200 hover:shadow-lg transition-all duration-300 text-center"
              >
                <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <service.icon className="h-8 w-8 text-primary-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{service.title}</h3>
                <p className="text-slate-600">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industry Expertise */}
      <section className="py-20 bg-white">
        <div className="container-consistent">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Deep Industry Expertise
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Our team includes industry veterans with decades of experience in your sector. 
                We understand your challenges because we've lived them.
              </p>
              
              <div className="space-y-6">
                {[
                  {
                    icon: Users,
                    title: 'Industry Veterans',
                    description: 'Team members with 15+ years in your specific industry'
                  },
                  {
                    icon: Shield,
                    title: 'Compliance Experts',
                    description: 'Specialists in FDA, HACCP, ISO, and industry standards'
                  },
                  {
                    icon: Zap,
                    title: 'Innovation Leaders',
                    description: 'Early adopters of new technologies and best practices'
                  }
                ].map((item, index) => (
                  <div key={item.title} className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <item.icon className="h-5 w-5 text-primary-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">{item.title}</h3>
                      <p className="text-slate-600">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-2xl p-8"
            >
              <h3 className="text-2xl font-bold text-slate-900 mb-6">Why Industry Expertise Matters</h3>
              
              <div className="space-y-4">
                {[
                  'Faster problem resolution',
                  'Industry-specific solutions',
                  'Regulatory compliance',
                  'Cost optimization',
                  'Risk mitigation',
                  'Competitive advantage'
                ].map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary-600 flex-shrink-0" />
                    <span className="text-slate-700">{benefit}</span>
            </div>
          ))}
        </div>
            </motion.div>
      </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800">
        <div className="container-consistent text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Transform Your Industry Logistics?
            </h2>
            <p className="text-lg text-primary-100 max-w-3xl mx-auto mb-8">
              Let our industry experts show you how CROSSDOCS can solve your unique 
              logistics challenges and drive operational excellence.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <NavLink 
                to="/contact" 
                className="inline-flex items-center rounded-xl bg-white px-8 py-4 text-base font-semibold text-primary-700 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
              >
                Get Industry Consultation
              </NavLink>
              <NavLink 
                to="/services" 
                className="inline-flex items-center rounded-xl px-8 py-4 text-base font-semibold text-white ring-2 ring-white/30 hover:ring-white/50 hover:bg-white/10 transition-all duration-300"
              >
                Explore Services
              </NavLink>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}






